/**
 * Utilitaires pour la validation des mots de passe
 * Selon les règles backend:
 * - Minimum 8 caractères
 * - Au moins une majuscule (A-Z)
 * - Au moins une minuscule (a-z)
 * - Au moins un chiffre (0-9)
 * - Au moins un caractère spécial parmi: @$!%*?&
 */

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  criteria: {
    minLength: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
    hasNumber: boolean;
    hasSpecialChar: boolean;
  };
}

/**
 * Regex de validation complète du mot de passe
 */
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

/**
 * Valide un mot de passe selon les critères du backend
 */
export const validatePassword = (password: string): PasswordValidationResult => {
  const criteria = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[@$!%*?&]/.test(password),
  };

  const errors: string[] = [];

  if (!criteria.minLength) {
    errors.push('Le mot de passe doit contenir au moins 8 caractères');
  }
  if (!criteria.hasUppercase) {
    errors.push('Le mot de passe doit contenir au moins une majuscule');
  }
  if (!criteria.hasLowercase) {
    errors.push('Le mot de passe doit contenir au moins une minuscule');
  }
  if (!criteria.hasNumber) {
    errors.push('Le mot de passe doit contenir au moins un chiffre');
  }
  if (!criteria.hasSpecialChar) {
    errors.push('Le mot de passe doit contenir au moins un caractère spécial (@$!%*?&)');
  }

  const isValid = PASSWORD_REGEX.test(password);

  return {
    isValid,
    errors,
    criteria,
  };
};

/**
 * Vérifie si le mot de passe est valide (version simplifiée)
 */
export const isPasswordValid = (password: string): boolean => {
  return PASSWORD_REGEX.test(password);
};

/**
 * Message d'erreur complet à afficher en cas de mot de passe invalide
 */
export const PASSWORD_VALIDATION_ERROR_MESSAGE =
  'Le mot de passe doit contenir au moins une majuscule, une minuscule, un chiffre et un caractère spécial';

/**
 * Helper pour afficher les caractères spéciaux autorisés
 */
export const ALLOWED_SPECIAL_CHARS = '@$!%*?&';
